import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { ClerkProvider } from '@clerk/clerk-react'
import posthog from 'posthog-js'
import './globals.css'

// Layout component
import RootLayout from './components/RootLayout.tsx'

// Error boundary
import AuthErrorBoundary from './components/AuthErrorBoundary.tsx'

// Clerk appearance configuration
import { clerkAppearance } from './lib/clerkAppearance.ts'

// API Client provider
import { ApiClientProvider } from './providers/ApiClientProvider.tsx'

// React Query provider
import { QueryClientProvider } from './providers/QueryClientProvider.tsx'

// Error provider
import { ErrorProvider } from './contexts/ErrorContext.tsx'

// Page components
import Games from './pages/Games.tsx'
import GameArchive from './pages/GameArchive.tsx'
import Friends from './pages/Friends.tsx'
import Stats from './pages/Stats.tsx'
import { GameComponentsDemo } from './pages/GameComponentsDemo.tsx'
import InviteHandler from './pages/InviteHandler.tsx'
import SsoPage from './pages/SsoPage.tsx'
import Subscription from './pages/Subscription.tsx'
import Help from './pages/Help.tsx'

// Auth components
import { RequireAuth } from './components/auth/RequireAuth.tsx'

// Game components
import { GameFrame } from './components/GameFrame'

// Get Clerk configuration
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;


if (!clerkPublishableKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in environment variables');
}

// Initialize PostHog before React renders
if (typeof window !== 'undefined') {
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
  
  // Check if PostHog should be enabled
  const isProduction = import.meta.env.MODE === 'production';
  const isDebugEnabled = import.meta.env.VITE_POSTHOG_DEBUG === 'true';
  
  // Enable PostHog if: in production OR debug is explicitly enabled
  if (posthogKey && (isProduction || isDebugEnabled)) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Privacy-first configuration
      autocapture: false, // Disable automatic event capture
      capture_pageview: 'history_change', // Automatically track SPA pageviews
      // Security settings
      secure_cookie: true,
      // Disable features that might capture PII
      disable_session_recording: true,
      // Development helpers
      debug: isDebugEnabled, // Enable debug logging when VITE_POSTHOG_DEBUG is true
      loaded: (posthog) => {
        // Ensure we're only tracking anonymously
        if (posthog.get_distinct_id()?.startsWith('user_')) {
          posthog.reset();
        }
        
        // Track initial hub visit
        if (!sessionStorage.getItem('hub_visit_tracked')) {
          posthog.capture('hub_visit', {
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
            },
            screen: {
              width: window.screen.width,
              height: window.screen.height,
            },
          });
          sessionStorage.setItem('hub_visit_tracked', 'true');
        }
      }
    });
  }
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Games /> // Public: Home route shows Games page
      },
      {
        path: 'games',
        element: <Games /> // Public: Games browsing page
      },
      {
        path: 'games/:gameId/archive',
        element: <GameArchive /> // Anonymous access allowed — displayState gates individual challenges
      },
      {
        path: 'games/:gameId/play/:challengeId',
        element: <GameFrame /> // Anonymous play supported (IMA-465)
      },
      {
        path: 'games/continuous/play/:challengeId',
        element: (
          <RequireAuth redirectTo="/games">
            <GameFrame />
          </RequireAuth>
        ) // Protected: Continuous game play requires authentication (challengeId = 0)
      },
      {
        path: 'friends',
        element: (
          <RequireAuth redirectTo="/friends">
            <Friends />
          </RequireAuth>
        ) // Protected: Friends page requires authentication
      },
      {
        path: 'stats',
        element: (
          <RequireAuth redirectTo="/stats">
            <Stats />
          </RequireAuth>
        ) // Protected: Stats page requires authentication
      },
      {
        path: 'demo/game-components',
        element: <GameComponentsDemo /> // Public: Demo page
      },
      {
        path: 'invite/:code',
        element: <InviteHandler /> // Public: Invite link handler
      },
      {
        path: 'sso',
        element: <SsoPage /> // Public: DMO sign-in token consumer
      },
      {
        path: 'subscription',
        element: <Subscription /> // Public: Subscription pricing page
      },
      {
        path: 'help',
        element: <Help /> // Public: Help and information page
      }
    ]
  }
])

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthErrorBoundary>
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        appearance={clerkAppearance}
        localization={{ badge__activePlan: 'Current Plan' }}
      >
        <ErrorProvider>
          <QueryClientProvider>
            <ApiClientProvider>
              <RouterProvider router={router} />
            </ApiClientProvider>
          </QueryClientProvider>
        </ErrorProvider>
      </ClerkProvider>
    </AuthErrorBoundary>
  </StrictMode>,
)
